// WP-1.1 (S-11 `/accounts`): cards grouped by platform group; name, currency, balance,
// spendable flag icon. ＋ Add account opens the create FormSheet.
import type { AccountResponse } from '@pfos/shared';
import { EyeOff, Plus, Wallet } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Amount } from '../components/Amount';
import { AccountForm } from '../components/AccountForm';
import { EmptyState } from '../components/EmptyState';
import { useAccounts } from '../lib/accounts';

const UNGROUPED = 'Accounts';

export function AccountsPage(): React.JSX.Element {
  const navigate = useNavigate();
  const { data: accounts, isLoading } = useAccounts();
  const [formOpen, setFormOpen] = useState(false);

  const groups = new Map<string, AccountResponse[]>();
  for (const account of accounts ?? []) {
    const key = account.platformGroup ?? UNGROUPED;
    const list = groups.get(key) ?? [];
    list.push(account);
    groups.set(key, list);
  }

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-xl font-semibold text-ink">Accounts</h1>
        <button
          className="flex items-center gap-1 rounded-[var(--radius-control)] bg-primary px-3 py-1.5 text-sm font-medium text-primary-ink hover:opacity-90 active:opacity-80"
          onClick={() => {
            setFormOpen(true);
          }}
        >
          <Plus size={16} /> Add account
        </button>
      </div>

      {isLoading && <p className="text-sm text-muted">Loading…</p>}

      {!isLoading && (accounts === undefined || accounts.length === 0) && (
        <EmptyState
          icon={Wallet}
          message="No accounts yet. Add one with ＋ to start tracking balances."
          action={{
            label: 'Add account',
            onClick: () => {
              setFormOpen(true);
            },
          }}
        />
      )}

      {[...groups.entries()].map(([group, items]) => (
        <div key={group} className="mb-6">
          <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted">{group}</p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((account) => (
              <button
                key={account.id}
                onClick={() => {
                  navigate(`/accounts/${account.id}`);
                }}
                className="rounded-[var(--radius-card)] border border-border bg-surface p-4 text-left hover:border-primary"
              >
                <div className="mb-2 flex items-center justify-between">
                  <span className="font-medium text-ink">{account.name}</span>
                  {!account.spendable && (
                    <EyeOff
                      size={16}
                      className="text-muted"
                      aria-label="Not counted in Spendable"
                    />
                  )}
                </div>
                <Amount
                  currency={account.currency}
                  minorUnits={account.balanceMinor}
                  className="text-lg font-semibold"
                />
                {account.archived && (
                  <span className="mt-2 block text-xs text-muted">Archived</span>
                )}
              </button>
            ))}
          </div>
        </div>
      ))}

      <AccountForm open={formOpen} onOpenChange={setFormOpen} />
    </div>
  );
}
